import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider } from "@/context/AuthContext";
import { ProtectedRoute } from "@/components/RouteGuards";
import AppShell from "@/components/layout/AppShell";
import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import RequestAccess from "@/pages/RequestAccess";
import AuthCallback from "@/pages/AuthCallback";
import Dashboard from "@/pages/Dashboard";
import SopLibrary from "@/pages/SopLibrary";
import SopDetail from "@/pages/SopDetail";
import SopEditor from "@/pages/SopEditor";
import AccessRequests from "@/pages/admin/AccessRequests";
import UsersPage from "@/pages/admin/Users";
import OrgStructure from "@/pages/admin/OrgStructure";
import MyTraining from "@/pages/MyTraining";
import Courses from "@/pages/Courses";
import CourseOverview from "@/pages/CourseOverview";
import LessonPlayer from "@/pages/LessonPlayer";
import CourseQuiz from "@/pages/CourseQuiz";
import CourseBuilder from "@/pages/CourseBuilder";
import Certificates from "@/pages/Certificates";
import Verify from "@/pages/Verify";
import Assignments from "@/pages/Assignments";
import Reports from "@/pages/Reports";
import PersonRecord from "@/pages/PersonRecord";
import AskLasso from "@/pages/AskLasso";
import AiSettings from "@/pages/admin/AiSettings";
import NotificationSettings from "@/pages/admin/NotificationSettings";
import Showcase from "@/pages/admin/Showcase";
import Profile from "@/pages/Profile";
import Forms from "@/pages/Forms";
import FormDetail from "@/pages/FormDetail";
import Feedback from "@/pages/Feedback";
import FeedbackInbox from "@/pages/admin/FeedbackInbox";
import Compliance from "@/pages/Compliance";
import FormRecords from "@/pages/FormRecords";
import FormRecordDetail from "@/pages/FormRecordDetail";
import FormRecordEditor from "@/pages/FormRecordEditor";
import EventTypes from "@/pages/admin/EventTypes";
import ExamInsightsPage from "@/pages/ExamInsights";

const MANAGERS = ["admin", "regional_manager", "community_manager"];

const AdminOnly = ({ children }) => <ProtectedRoute roles={["admin"]}>{children}</ProtectedRoute>;
const AdminOrRA = ({ children }) => <ProtectedRoute roles={["admin"]} regionAdmin>{children}</ProtectedRoute>;
const SuperOnly = ({ children }) => <ProtectedRoute roles={["admin"]} superOnly>{children}</ProtectedRoute>;

function AppRouter() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/login" element={<Login />} />
      <Route path="/request-access" element={<RequestAccess />} />
      <Route path="/verify" element={<Verify />} />
      <Route path="/verify/:code" element={<Verify />} />
      <Route element={<ProtectedRoute><AppShell /></ProtectedRoute>}>
        <Route path="/dashboard" element={<Dashboard />} />
        <Route path="/sops" element={<SopLibrary />} />
        <Route path="/sops/new" element={<AdminOrRA><SopEditor /></AdminOrRA>} />
        <Route path="/sops/:id" element={<SopDetail />} />
        <Route path="/sops/:id/edit" element={<AdminOrRA><SopEditor /></AdminOrRA>} />
        <Route path="/training" element={<MyTraining />} />
        <Route path="/ask" element={<AskLasso />} />
        <Route path="/ask/:cid" element={<AskLasso />} />
        <Route path="/admin/ai" element={<SuperOnly><AiSettings /></SuperOnly>} />
        <Route path="/admin/notifications" element={<SuperOnly><NotificationSettings /></SuperOnly>} />
        <Route path="/admin/showcase" element={<AdminOnly><Showcase /></AdminOnly>} />
        <Route path="/profile" element={<Profile />} />
        <Route path="/forms" element={<Forms />} />
        <Route path="/forms/:id" element={<FormDetail />} />
        <Route path="/feedback" element={<ProtectedRoute roles={["admin", "regional_manager", "community_manager"]}><Feedback /></ProtectedRoute>} />
        <Route path="/admin/feedback" element={<AdminOnly><FeedbackInbox /></AdminOnly>} />
        <Route path="/compliance" element={<ProtectedRoute roles={["admin", "regional_manager"]}><Compliance /></ProtectedRoute>} />
        <Route path="/form-records" element={<ProtectedRoute roles={MANAGERS}><FormRecords /></ProtectedRoute>} />
        <Route path="/form-records/new" element={<ProtectedRoute roles={MANAGERS}><FormRecordEditor /></ProtectedRoute>} />
        <Route path="/form-records/:id" element={<ProtectedRoute roles={MANAGERS}><FormRecordDetail /></ProtectedRoute>} />
        <Route path="/form-records/:id/edit" element={<ProtectedRoute roles={MANAGERS}><FormRecordEditor /></ProtectedRoute>} />
        <Route path="/insights/exams" element={<AdminOrRA><ExamInsightsPage /></AdminOrRA>} />
        <Route path="/insights/exams/:id" element={<AdminOrRA><ExamInsightsPage /></AdminOrRA>} />
        <Route path="/admin/event-types" element={<AdminOnly><EventTypes /></AdminOnly>} />
        <Route path="/courses" element={<Courses />} />
        <Route path="/courses/new" element={<AdminOrRA><CourseBuilder /></AdminOrRA>} />
        <Route path="/courses/:id" element={<CourseOverview />} />
        <Route path="/courses/:id/edit" element={<AdminOrRA><CourseBuilder /></AdminOrRA>} />
        <Route path="/courses/:id/learn/:lessonId" element={<LessonPlayer />} />
        <Route path="/courses/:id/quiz" element={<CourseQuiz />} />
        <Route path="/certificates" element={<Certificates />} />
        <Route path="/assignments" element={<ProtectedRoute roles={["admin", "regional_manager"]}><Assignments /></ProtectedRoute>} />
        <Route path="/reports" element={<ProtectedRoute roles={MANAGERS}><Reports /></ProtectedRoute>} />
        <Route path="/reports/people/:id" element={<ProtectedRoute roles={MANAGERS}><PersonRecord /></ProtectedRoute>} />
        <Route path="/team" element={<ProtectedRoute roles={["regional_manager"]}><UsersPage readOnly /></ProtectedRoute>} />
        <Route path="/admin/requests" element={<AdminOrRA><AccessRequests /></AdminOrRA>} />
        <Route path="/admin/users" element={<AdminOrRA><UsersPage /></AdminOrRA>} />
        <Route path="/admin/org" element={<AdminOnly><OrgStructure /></AdminOnly>} />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <AppRouter />
          <Toaster position="top-right" richColors />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
